import { useStore } from "@tanstack/react-form";
import { CATALOG_STATUS_OPTIONS } from "@/features/catalog/lib/catalog-status-options";
import { useAppForm } from "@/shared/forms/app-form";
import type { StatusPickerOption } from "@/shared/status/status-picker-utils";
import type { DefinitionShell } from "./catalog-definition-context";

/**
 * The three fields the shell owns. Everything else on a definition — steps,
 * findings, address, observation schema — belongs to the kind's own builder
 * form, so that a shell-rendered Title and a builder-rendered `title` are
 * never two sources of truth for the same value.
 *
 * `status` is the kind's OWN code, not the collapsed display value: locations
 * are suspended, devices are inactive, protocols are retired. The collapse to
 * Active / Inactive / Draft happens in `toShell`, for the badge only.
 */
export interface ShellFormValues {
  title: string;
  description: string;
  status: string;
}

export type ShellFieldName = keyof ShellFormValues;

export interface UseShellFormOptions {
  /** The kind's resource mapped through its `toShell`. */
  shell: DefinitionShell;
  /** Raw editable values. Defaults to the shell's own title/description/status. */
  values?: Partial<ShellFormValues>;
  /** The kind's status vocabulary. Locations offer suspended; questionnaires have no draft. */
  statusOptions?: StatusPickerOption[];
  /** Explicit save, for the builder pages that have a Save button. */
  onSubmit?: (values: ShellFormValues) => Promise<void> | void;
}

export function useShellForm({ shell, values, statusOptions, onSubmit }: UseShellFormOptions) {
  const form = useAppForm({
    defaultValues: {
      title: values?.title ?? shell.title,
      description: values?.description ?? shell.description ?? "",
      status: values?.status ?? shell.status,
    } satisfies ShellFormValues,
    onSubmit: async ({ value }) => {
      await onSubmit?.(value);
    },
  });

  return {
    /**
     * The non-editable half of the shell — kindLabel, version, lastUpdated.
     * Title / description / status live in `form`, not here.
     */
    shell,
    form,
    statusOptions: statusOptions ?? CATALOG_STATUS_OPTIONS,
  };
}

export type ShellForm = ReturnType<typeof useShellForm>;

/**
 * Has anything changed across the shell form and the kind's own form?
 *
 * Splitting title/description/status out of the builders means neither form
 * alone knows whether the page is dirty — the shell has to ask both, or Save
 * lights up for an edit the user did not make.
 */
export function useDefinitionDirty(shellForm: ShellForm, kindForm: DirtyTrackableForm): boolean {
  const shellDirty = useShellDirty(shellForm);
  const kindDirty = useStore(kindForm.store, (s) => !(s as { isDefaultValue: boolean }).isDefaultValue);
  return shellDirty || kindDirty;
}

/**
 * Just the shell half. For a kind whose builder tracks its own dirty state —
 * the questionnaire builder keeps it in an xstate store, not a form — combine
 * this with that flag rather than pretending it is a TanStack form.
 */
export function useShellDirty(shellForm: ShellForm): boolean {
  return useStore(shellForm.form.store, (s) => !s.isDefaultValue);
}

/**
 * Only `isDefaultValue` is read, and the eight kind forms have eight different
 * value types — naming them all here would buy nothing but a union to maintain.
 */
// biome-ignore lint/suspicious/noExplicitAny: see above
type DirtyTrackableForm = { store: any };
